/**
 * The whole customer experience: one page, no auth. Reads the token from the
 * URL (/job/<token>), fetches the job, and shows status + details + (when the
 * driver is en route) a live map that polls every few seconds.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ATTACH_ACCEPT, ATTACH_MAX_BYTES, COMMENT_MAX_CHARS,
  fetchCommentary, fetchJob, fetchLocation, postComment,
  type JobResult, type PublicJob, type PublicJobComment, type VehicleLocation,
} from './api';
import { GOOGLE_MAPS_KEY, LOCATION_POLL_MS } from './config';
import { Icon } from './Icon';
import { FONT, statusPill, t, WEBSITE_URL } from './theme';
import { VehicleMap } from './VehicleMap';

/** Brand header — the app logo asset + wordmark, linked to the marketing site. */
function Header() {
  return (
    <a href={WEBSITE_URL} style={s.header} aria-label="Axlerun home">
      <img src="/logo.png" alt="" width={30} height={30} style={{ borderRadius: 7, display: 'block' }} />
      <span style={s.wordmark}>Axlerun</span>
    </a>
  );
}

/** Token is the last non-empty path segment (…/job/<token>); falls back to ?token=. */
function readToken(): string | null {
  const parts = window.location.pathname.split('/').filter(Boolean);
  const last = parts[parts.length - 1];
  if (last && last !== 'job') return decodeURIComponent(last);
  const q = new URLSearchParams(window.location.search).get('token');
  return q ? decodeURIComponent(q) : null;
}

function fmtDateTime(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-AU', {
    weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
  });
}

function trackingStatuses(status?: string | null): boolean {
  return status === 'EnRoute' || status === 'Arrived' || status === 'InProgress';
}

export default function App() {
  const token = readToken();
  const isDemo = new URLSearchParams(window.location.search).get('demo') === '1';
  const [result, setResult] = useState<JobResult | null>(null);
  const [vehicle, setVehicle] = useState<VehicleLocation | null>(null);
  // If Maps fails to authorise (bad key / referrer not allowed), hide the map
  // rather than let Google render its grey "Something went wrong" box.
  const [mapError, setMapError] = useState(false);
  const pollRef = useRef<number | null>(null);

  const load = useCallback(async () => {
    // `?demo=1` renders a fake job so the layout can be reviewed without a real
    // token. Dev/design aid only — a real token always goes to the API.
    if (new URLSearchParams(window.location.search).get('demo') === '1') {
      setResult({ kind: 'ok', job: DEMO_JOB });
      setVehicle(DEMO_JOB.vehicleLocation ?? null);
      return;
    }
    if (!token) { setResult({ kind: 'invalid' }); return; }
    const r = await fetchJob(token);
    setResult(r);
    if (r.kind === 'ok') setVehicle(r.job.vehicleLocation ?? null);
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  // Poll the live location only while the driver is actively en route.
  const job = result?.kind === 'ok' ? result.job : null;
  const shouldPoll = !!token && !!job && trackingStatuses(job.status);
  useEffect(() => {
    if (!shouldPoll || !token) return;
    const tick = async () => {
      const loc = await fetchLocation(token);
      if (loc) setVehicle(loc);
    };
    pollRef.current = window.setInterval(tick, LOCATION_POLL_MS);
    return () => { if (pollRef.current) window.clearInterval(pollRef.current); };
  }, [shouldPoll, token]);

  if (result === null) return <Centered>Loading…</Centered>;
  if (result.kind === 'invalid') {
    return <Message icon="🔒" title="Link not found" body="This link is invalid or has expired. Please contact the business for an updated link." />;
  }
  if (result.kind === 'done') {
    return <Message icon="✓" title="Job complete" body="This job has been completed. Thank you for using Axlerun." />;
  }
  if (result.kind === 'error') {
    return <Message icon="!" title="Something went wrong" body="Couldn’t load this job. Check your connection and try again." action={{ label: 'Retry', onClick: () => { setResult(null); void load(); } }} />;
  }

  const j: PublicJob = result.job;
  const pill = statusPill(j.status);
  const address = [j.location?.addressLine1, j.location?.suburb, j.location?.state].filter(Boolean).join(', ');
  const showMap = !!GOOGLE_MAPS_KEY && !!vehicle && !mapError && trackingStatuses(j.status);

  return (
    <div style={s.page}>
      <Header />
      <div style={s.card}>
        <div style={s.badgeRow}>
          <span style={{ ...s.badge, background: pill.bg, color: pill.fg }}>{pill.label}</span>
        </div>
        <h1 style={s.title}>{j.title || 'Your job'}</h1>
        {!!j.scheduledStartUtc && <p style={s.sub}>Scheduled for {fmtDateTime(j.scheduledStartUtc)}</p>}

        {showMap && vehicle && (
          <div style={{ marginTop: 16 }}>
            <VehicleMap vehicle={vehicle} destination={j.location} onError={() => setMapError(true)} />
            <p style={s.mapHint}>Your driver is on the way — this updates live.</p>
          </div>
        )}

        {!!j.description && (
          <Field label="Details" value={j.description} />
        )}
        {!!address && <Field label="Location" value={address} />}
        {!!j.customerName && <Field label="Customer" value={j.customerName} />}

        {j.commentaryEnabled && (
          <Commentary token={token ?? 'demo'} seed={isDemo ? DEMO_COMMENTS : undefined} />
        )}
      </div>
      <Footer />
    </div>
  );
}

function Footer() {
  return (
    <a href={WEBSITE_URL} style={s.footer}>Powered by Axlerun</a>
  );
}

function fmtTime(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}

/**
 * Customer-facing message thread. Fetches once, refetches after the customer
 * sends, and polls slowly so a reply from the business appears without a manual
 * refresh. `seed` short-circuits the network for the `?demo=1` layout preview.
 */
function isImage(contentType?: string | null, fileName?: string | null): boolean {
  if (contentType) return contentType.startsWith('image/');
  return /\.(jpe?g|png|heic|webp|gif)$/i.test(fileName ?? '');
}

function Commentary({ token, seed }: { token: string; seed?: PublicJobComment[] }) {
  const isDemo = seed !== undefined;
  const [items, setItems] = useState<PublicJobComment[] | null>(seed ?? null);
  const [draft, setDraft] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState<'generic' | 'rate' | null>(null);
  const [tooBig, setTooBig] = useState(false);
  // Two inputs: `capture` opens the camera on mobile, the other the photo
  // library / file picker. The browser handles the camera permission prompt.
  const cameraRef = useRef<HTMLInputElement>(null);
  const libraryRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    if (isDemo) return;
    const list = await fetchCommentary(token);
    if (list) setItems(list);
    else setItems((prev) => prev ?? []); // resolve the loading state even on failure
  }, [token, isDemo]);

  useEffect(() => { void refresh(); }, [refresh]);

  // Slow poll so a business reply lands on its own — a thread, not a one-shot.
  useEffect(() => {
    if (isDemo) return;
    const id = window.setInterval(() => { void refresh(); }, 20000);
    return () => window.clearInterval(id);
  }, [refresh, isDemo]);

  const addFiles = (picked: FileList | null) => {
    if (!picked || picked.length === 0) return;
    const next: File[] = [];
    let rejected = false;
    for (const f of Array.from(picked)) {
      if (f.size > ATTACH_MAX_BYTES) { rejected = true; continue; }
      next.push(f);
    }
    setTooBig(rejected);
    if (next.length) setFiles((prev) => [...prev, ...next]);
  };

  const removeFile = (idx: number) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  // Local object-URL previews for pending images, so the draft shows the actual
  // photo (not just a filename) — makes clear it's queued, not yet sent. The
  // cleanup revokes the previous set whenever `files` changes or on unmount.
  const previews = useMemo(
    () => files.map((f) => (isImage(f.type, f.name) ? URL.createObjectURL(f) : null)),
    [files],
  );
  useEffect(() => () => { previews.forEach((u) => u && URL.revokeObjectURL(u)); }, [previews]);

  const send = async () => {
    const message = draft.trim();
    if ((!message && files.length === 0) || sending) return;
    setSending(true);
    setFailed(null);
    if (isDemo) {
      setItems((prev) => [...(prev ?? []), {
        jobCommentaryId: `demo-${(prev?.length ?? 0)}`, message,
        isFromCustomer: true, authorLabel: 'You', createdAt: new Date().toISOString(),
        attachments: files.map((f) => ({ fileName: f.name, contentType: f.type, secureFileUrl: URL.createObjectURL(f) })),
      }]);
      setDraft(''); setFiles([]); setSending(false);
      return;
    }
    const res = await postComment(token, message, files);
    if (res.ok) {
      setDraft(''); setFiles([]);
      await refresh();
    } else {
      setFailed(res.rateLimited ? 'rate' : 'generic');
    }
    setSending(false);
  };

  if (items === null) return null; // still loading the first fetch — keep the card quiet

  const canSend = (!!draft.trim() || files.length > 0) && !sending;

  return (
    <div style={s.thread}>
      <div style={s.fieldLabel}>Messages</div>
      {items.length === 0 && (
        <p style={{ ...s.sub, margin: '2px 0 12px' }}>
          Send a message or photo to the team about this job — they’ll see it right away.
        </p>
      )}
      {items.map((c) => {
        const mine = !!c.isFromCustomer;
        const atts = c.attachments ?? [];
        return (
          <div key={c.jobCommentaryId} style={{ ...s.bubbleRow, justifyContent: mine ? 'flex-end' : 'flex-start' }}>
            <div style={{ ...s.bubble, ...(mine ? s.bubbleMine : s.bubbleTheirs) }}>
              {!mine && !!c.authorLabel && <div style={s.bubbleAuthor}>{c.authorLabel}</div>}
              {atts.length > 0 && (
                <div style={s.attGrid}>
                  {atts.map((a, i) => (
                    a.secureFileUrl && isImage(a.contentType, a.fileName) ? (
                      <a key={i} href={a.secureFileUrl} target="_blank" rel="noreferrer">
                        <img src={a.secureFileUrl} alt={a.fileName ?? 'attachment'} style={s.attThumb} />
                      </a>
                    ) : (
                      <a key={i} href={a.secureFileUrl ?? '#'} target="_blank" rel="noreferrer"
                         style={{ ...s.attFile, color: mine ? '#fff' : t.brand }}>
                        <Icon name="document" size={15} /> {a.fileName ?? 'Attachment'}
                      </a>
                    )
                  ))}
                </div>
              )}
              {!!c.message && <div style={s.bubbleText}>{c.message}</div>}
              <div style={{ ...s.bubbleTime, color: mine ? 'rgba(255,255,255,0.75)' : t.textMuted }}>
                {fmtTime(c.createdAt)}
              </div>
            </div>
          </div>
        );
      })}

      {files.length > 0 && (
        <div style={s.pendingWrap}>
          <div style={s.pendingRow}>
            {files.map((f, i) => (
              <span key={i} style={s.pendingItem}>
                {previews[i] ? (
                  <img src={previews[i]!} alt={f.name} style={s.pendingThumb} />
                ) : (
                  <span style={s.pendingDoc} title={f.name}>
                    <Icon name="document" size={18} color={t.textSecondary} />
                    <span style={s.pendingDocName}>{f.name.length > 10 ? f.name.slice(0, 8) + '…' : f.name}</span>
                  </span>
                )}
                <button style={s.pendingRemove} onClick={() => removeFile(i)} aria-label={`Remove ${f.name}`}>×</button>
              </span>
            ))}
          </div>
          {/* Make the state unambiguous: the file is queued, not yet uploaded —
              it goes when you press Send. */}
          <div style={s.pendingHint}>Attached — sends when you press Send</div>
        </div>
      )}

      <div style={s.composer}>
        <input ref={cameraRef} type="file" accept="image/*" capture="environment"
               style={{ display: 'none' }} onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
        <input ref={libraryRef} type="file" accept={ATTACH_ACCEPT} multiple
               style={{ display: 'none' }} onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
        <button style={s.iconBtn} onClick={() => cameraRef.current?.click()} aria-label="Take a photo" title="Take a photo">
          <Icon name="camera" size={20} color={t.textSecondary} />
        </button>
        <button style={s.iconBtn} onClick={() => libraryRef.current?.click()} aria-label="Attach a photo or file" title="Attach a photo or file">
          <Icon name="attach" size={20} color={t.textSecondary} />
        </button>
        <textarea
          style={s.input}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } }}
          placeholder="Write a message…"
          rows={1}
          maxLength={COMMENT_MAX_CHARS}
        />
        <button
          style={{ ...s.sendBtn, opacity: canSend ? 1 : 0.5 }}
          onClick={() => void send()}
          disabled={!canSend}
          aria-label="Send message"
        >
          {sending ? '…' : 'Send'}
        </button>
      </div>
      {tooBig && <p style={s.sendError}>Some files were over 10&nbsp;MB and weren’t added.</p>}
      {failed === 'rate' && <p style={s.sendError}>You’re sending messages too quickly — please wait a moment and try again.</p>}
      {failed === 'generic' && <p style={s.sendError}>Couldn’t send — please try again.</p>}
    </div>
  );
}

const DEMO_COMMENTS: PublicJobComment[] = [
  { jobCommentaryId: 'd1', message: 'Hi Jane — we’re on our way, about 15 minutes out.', isFromCustomer: false, authorLabel: 'Manly Plumbing', createdAt: new Date(Date.now() - 9 * 60000).toISOString() },
  { jobCommentaryId: 'd2', message: 'Great, thanks! The side gate will be unlocked.', isFromCustomer: true, authorLabel: 'You', createdAt: new Date(Date.now() - 6 * 60000).toISOString() },
];

const DEMO_JOB: PublicJob = {
  jobId: 'demo',
  title: 'Hot water system replacement',
  description: 'Old unit leaking in the roof cavity. Access via the rear lane.',
  status: 'EnRoute',
  scheduledStartUtc: new Date().toISOString(),
  customerName: 'Jane Smith',
  location: { addressLine1: '12 Marine Pde', suburb: 'Manly', state: 'NSW', latitude: -33.7969, longitude: 151.2876 },
  vehicleLocation: { latitude: -33.8, longitude: 151.28, heading: 180, speedKmh: 42, recordedAtUtc: new Date().toISOString() },
  commentaryEnabled: true,
};

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div style={s.field}>
      <div style={s.fieldLabel}>{label}</div>
      <div style={s.fieldValue}>{value}</div>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div style={{ ...s.page, justifyContent: 'center', color: t.textSecondary }}>{children}</div>;
}

function Message({ icon, title, body, action }: {
  icon: string; title: string; body: string; action?: { label: string; onClick: () => void };
}) {
  return (
    <div style={s.page}>
      <Header />
      <div style={{ ...s.card, textAlign: 'center', maxWidth: 380, marginTop: 40 }}>
        <div style={s.msgIcon}>{icon}</div>
        <h1 style={s.title}>{title}</h1>
        <p style={s.sub}>{body}</p>
        {action && <button style={s.btn} onClick={action.onClick}>{action.label}</button>}
      </div>
      <div style={{ marginTop: 'auto' }}><Footer /></div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100dvh', background: t.paper, display: 'flex', flexDirection: 'column',
    alignItems: 'center', padding: '20px 16px 28px', boxSizing: 'border-box', fontFamily: FONT,
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 9, textDecoration: 'none',
    marginBottom: 20, alignSelf: 'flex-start', width: '100%', maxWidth: 460,
  },
  wordmark: { fontFamily: FONT, fontSize: 19, fontWeight: 800, color: t.charcoal, letterSpacing: -0.2 },
  card: {
    width: '100%', maxWidth: 460, background: t.card, borderRadius: 18,
    padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', boxSizing: 'border-box',
  },
  badgeRow: { marginBottom: 10 },
  badge: { fontSize: 13, fontWeight: 600, padding: '4px 12px', borderRadius: 999 },
  title: { fontSize: 22, fontWeight: 600, color: t.charcoal, margin: '0 0 4px' },
  sub: { fontSize: 14, color: t.textSecondary, margin: 0, lineHeight: 1.5 },
  mapHint: { fontSize: 13, color: t.textSecondary, margin: '8px 2px 0' },
  field: { borderTop: `1px solid ${t.border}`, paddingTop: 12, marginTop: 14 },
  fieldLabel: { fontSize: 12, color: t.textMuted, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 3 },
  fieldValue: { fontSize: 15, color: t.charcoal, lineHeight: 1.5 },
  footer: { fontSize: 12, color: t.textMuted, marginTop: 22, textDecoration: 'none' },
  thread: { borderTop: `1px solid ${t.border}`, paddingTop: 14, marginTop: 16 },
  bubbleRow: { display: 'flex', marginTop: 8 },
  bubble: { maxWidth: '82%', padding: '8px 12px', borderRadius: 14, boxSizing: 'border-box' },
  bubbleMine: { background: t.brand, color: '#fff', borderBottomRightRadius: 4 },
  bubbleTheirs: { background: t.paper, color: t.charcoal, borderBottomLeftRadius: 4, border: `1px solid ${t.border}` },
  bubbleAuthor: { fontSize: 11, fontWeight: 700, color: t.textSecondary, marginBottom: 2 },
  bubbleText: { fontSize: 14, lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  bubbleTime: { fontSize: 11, marginTop: 3, textAlign: 'right' },
  attGrid: { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 },
  attThumb: { width: 96, height: 96, objectFit: 'cover', borderRadius: 10, display: 'block' },
  attFile: { fontSize: 13, fontWeight: 600, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 0' },
  pendingWrap: { marginTop: 12 },
  pendingRow: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  pendingItem: { position: 'relative', display: 'inline-flex' },
  pendingThumb: {
    width: 56, height: 56, objectFit: 'cover', borderRadius: 10, display: 'block',
    border: `1px solid ${t.border}`,
  },
  pendingDoc: {
    display: 'inline-flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
    width: 56, height: 56, borderRadius: 10, background: t.paper, border: `1px solid ${t.border}`,
  },
  pendingDocName: { fontSize: 9, color: t.textSecondary, maxWidth: 48, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  pendingRemove: {
    position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: 999,
    border: `1px solid ${t.border}`, background: t.card, color: t.textSecondary, cursor: 'pointer',
    fontSize: 14, lineHeight: '18px', padding: 0, boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
  },
  pendingHint: { fontSize: 12, color: t.textMuted, marginTop: 6 },
  composer: { display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 14 },
  iconBtn: {
    border: `1px solid ${t.border}`, background: t.card, borderRadius: 12, cursor: 'pointer',
    fontSize: 18, lineHeight: 1, padding: '9px 10px', flexShrink: 0,
  },
  input: {
    flex: 1, resize: 'none', fontFamily: FONT, fontSize: 14, color: t.charcoal,
    border: `1px solid ${t.border}`, borderRadius: 12, padding: '10px 12px', outline: 'none',
    lineHeight: 1.4, background: t.card, boxSizing: 'border-box', maxHeight: 120,
  },
  sendBtn: {
    background: t.brand, color: '#fff', border: 'none', borderRadius: 12,
    padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
  },
  sendError: { fontSize: 12, color: '#B4231F', margin: '6px 2px 0' },
  msgIcon: { fontSize: 34, marginBottom: 8 },
  btn: {
    marginTop: 16, background: t.brand, color: '#fff', border: 'none', borderRadius: 12,
    padding: '10px 20px', fontSize: 15, fontWeight: 600, cursor: 'pointer',
  },
};
